import { useState, type ReactNode } from 'react';
import { Button, Mascot, Modal } from '@redpandev/ui';
import { promptInstall } from '../../lib/pwa-install';

export type InstallPromptCloseReason = 'installed' | 'later' | 'backdrop';

export interface InstallPromptProps {
  open: boolean;
  /** `other` n'affiche jamais de popin (l'appelant filtre en amont). */
  platform: 'chromium' | 'ios';
  onClose: (reason: InstallPromptCloseReason) => void;
}

/** Glyphe « Partager » d'iOS (carré + flèche vers le haut) — décoratif. */
function ShareGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none">
      <path
        d="M12 3v11M8 6l4-4 4 4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 11H5.5A1.5 1.5 0 0 0 4 12.5v6A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5v-6A1.5 1.5 0 0 0 18.5 11H17"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Glyphe « Sur l'écran d'accueil » (carré arrondi + « + ») — décoratif. */
function AddToHomeGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none">
      <rect x="4" y="4" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 8.5v7M8.5 12h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function IosStep({
  index,
  glyph,
  children,
}: {
  index: number;
  glyph: ReactNode;
  children: ReactNode;
}) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-panda-500/15 font-display text-sm font-bold text-panda-500">
        {index}
      </span>
      <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-ink-600/60 bg-ink-700/60 text-ink-100">
        {glyph}
      </span>
      <p className="text-sm text-ink-200">{children}</p>
    </li>
  );
}

/**
 * Popin d'installation (spec 12 §4), sur la base de `Modal` (focus piégé, ESC, aria).
 * Deux variantes : Chromium (prompt natif) et iOS (tutoriel « Sur l'écran d'accueil »,
 * aucune API). Le bouton « Plus tard » est toujours visible.
 */
export function InstallPrompt({ open, platform, onClose }: InstallPromptProps) {
  const [installing, setInstalling] = useState(false);

  const handleInstall = async () => {
    setInstalling(true);
    try {
      const outcome = await promptInstall();
      onClose(outcome === 'accepted' ? 'installed' : 'later');
    } finally {
      setInstalling(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => onClose('backdrop')}
      size="sm"
      title={
        <span className="flex items-center gap-2">
          <Mascot mood="excited" size={28} /> Installe RedPan 🐾
        </span>
      }
    >
      {platform === 'chromium' ? (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-ink-200">
            Ajoute RedPan à ton écran : lancement en plein écran, une icône rien qu'à toi, et
            bientôt les notifications de tes parties.
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={() => onClose('later')}>
              Plus tard
            </Button>
            <Button onClick={handleInstall} loading={installing}>
              Installer
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-ink-200">
            En deux tapotements, RedPan rejoint ton écran d'accueil :
          </p>
          <ol className="flex flex-col gap-3">
            <IosStep index={1} glyph={<ShareGlyph />}>
              Touche l'icône <b>Partager</b> en bas de l'écran.
            </IosStep>
            <IosStep index={2} glyph={<AddToHomeGlyph />}>
              Choisis <b>« Sur l'écran d'accueil »</b>, puis <b>Ajouter</b>.
            </IosStep>
          </ol>
          <p className="text-xs text-ink-400">
            Sur Chrome, Edge ou Firefox iOS, le menu Partager fonctionne aussi (iOS 16.4+).
          </p>
          <div className="flex justify-end">
            <Button onClick={() => onClose('later')}>J'ai compris</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
